import React, { FC } from 'react';
import { useRouter } from 'next/router';
import { accResultsKeys, useApplyTest, useApplyTutorial } from '@components/acca/test/query/useApply';
import VerificationModal from '@components/acca/test/modals/verification';
import NoMoreChanceModal from '@components/acca/test/modals/noMoreChance';
import TestGuideModal from '@components/acca/test/modals/testGuide';
import TermsModal from '@components/acca/test/modals/terms';
import LoginModal from '@components/loginModal';
import DeleteApplyErrorModal from '@components/acca/test/modals/deleteApplyErrorModal';
import { useUserInfo } from '@repository/auth/useUserInfo';
import { useAchievementLog } from '@repository/match/logging/useLogging';
import { TERMS } from '@domain/constant/v1/termsType';
import { AccaModalType } from '@domain/constant/acca/AccaModalType';
import { RouteUrl, V2Route } from '@utils/routeUtil';
import useDebounce from '@utils/hooks/useDebounce';
import { queryClient } from '@utils/queryClient';
import UrlUtil from '@utils/urlUtil';

interface IAccaModalProps {
  modalState: AccaModalType;
  setModalState: (type: AccaModalType) => void;
}

const AccaModal: FC<IAccaModalProps> = ({ modalState, setModalState }) => {
  const { push, query, pathname } = useRouter();

  const isVisible = (modalType: AccaModalType) => modalType === modalState;
  const { data: userInfo } = useUserInfo();
  const userName = userInfo?.name ?? '';
  const isLogin = !!userInfo?.id;

  const { mutateAsync: saveAchievementLogAsync } = useAchievementLog();
  const { mutateAsync: testApplyMutateAsync } = useApplyTest(() => {
    query.source_page === 'MATCH' &&
      saveAchievementLogAsync({ achievementType: 'ACC_APPLY', sourcePage: query.source_page });
  });
  const { refetch } = useApplyTutorial(false);

  const onClose = () => {
    setModalState(null);
  };

  // 약관 동의 버튼 이벤트
  const onApplyTerms = async () => {
    onClose();
    if (pathname !== RouteUrl.ACCA_TEST_LIST) {
      push({ pathname: RouteUrl.ACCA_TEST_LIST, query });
    }
  };

  //응시 하기 버튼 이벤트
  const onApplyTest = useDebounce(async () => {
    const rs = await testApplyMutateAsync();
    const { url, code: authenticationCode } = rs;

    UrlUtil.openWindowWithPost(url, {
      authenticationCode,
      name: userName,
    });

    //최근 응시 상태 최신화
    await queryClient.refetchQueries(accResultsKeys, { active: true, exact: true });
    onClose();
  }, 1000);

  //튜토리얼 응시 하기 버튼 이벤트
  const onApplyTutorial = async () => {
    await refetch().then(({ data }) => {
      const { url, token } = data;
      const accTutorialUrl = V2Route.getAccaTutorialPage(url);
      UrlUtil.openWindowWithPost(accTutorialUrl, { Authorization: `Bearer ${token}` });

      onClose();
    });
  };

  return (
    <>
      {/* 본인 인증 모달 */}
      <VerificationModal visible={isVisible('VERIFICATION')} onClose={onClose} />

      {/* 응시기회 소진 모달 */}
      <NoMoreChanceModal visible={isVisible('NO_MORE_CHANCE')} onClose={onClose} />

      {/*응시 가이드 모달*/}
      <TestGuideModal visible={isVisible('TEST_GUIDE')} onClose={onClose} onApply={onApplyTest} />

      {/*응시 약관 모달(다음 동작 : 실전 응시하기 게이트 이동) */}
      <TermsModal
        termsType={TERMS.ACC_PRIVACY_POLICY}
        visible={isVisible('TEST_TERMS')}
        onClose={onClose}
        onConfirm={onApplyTerms}
      />

      {/*응시 약관 모달(다음 동작 : 바로 응시)*/}
      <TermsModal
        termsType={TERMS.ACC_PRIVACY_POLICY}
        visible={isVisible('TEST_TERMS_DIRECT')}
        onClose={onClose}
        onConfirm={onApplyTest}
      />

      {/*응시 튜토리얼 약관 모달*/}
      <TermsModal
        termsType={TERMS.ACC_TRIAL_PRIVACY_POLICY}
        visible={isVisible('TUTORIAL_TERMS')}
        onClose={onClose}
        onConfirm={onApplyTutorial}
      />

      <LoginModal
        subTitle="채용정보 · 역량검사 · 매칭까지"
        title="취업기회 지금 바로 잡다!"
        visible={isVisible('LOGIN')}
        onClose={onClose}
      />

      <DeleteApplyErrorModal visible={isVisible('DELETE_APPLY_CONTINUE_ERROR')} onClose={onClose} />
    </>
  );
};

export default AccaModal;
